import React from 'react'
import { useTranslation } from 'react-i18next'

import Banner from '@/components-latest/primitives/Banner'
import Button from '@/components-latest/primitives/Button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components-latest/primitives/Collapsible'
import Checkbox from '@/components-latest/primitives/Controls/Checkbox'
import TimePicker from '@/components-latest/primitives/Controls/TimePicker'
import {
  ArrowDown,
  Close,
  InfoCircle,
  Plus
} from '@/components-latest/primitives/Icons'
import Label from '@/components-latest/primitives/Label'
import Separator from '@/components-latest/primitives/Separator'
import Text from '@/components-latest/primitives/Text'
import { Form, FormControl, FormField } from '@/components-latest/shared/Form'
import { useBusinessHoursForm5Slots } from '@/components-latest/ui/AccountPage/BusinessHoursForm5Slots/useBusinessHoursForm5Slots'
import { ApiGetBusinessHours } from '@/ducks/account/account.types'

import Styles from './BusinessHoursForm5Slots.module.scss'

interface IBusinessFormProps {
  initialData: ApiGetBusinessHours
  canEdit?: boolean
}

const BusinessHoursForm5Slots: React.FC<IBusinessFormProps> = ({
  initialData,
  canEdit = true
}) => {
  const { t } = useTranslation()
  const startTimeLabelKey = 'account.business-hours.time-label-start'
  const endTimeLabelKey = 'account.business-hours.time-label-end'

  const {
    form,
    handleDeleteSlot,
    handleAddSlot,
    handleSubmit,
    getFormattedValue,
    shouldDisableAdd,
    getMixMaxRules,
    errorMessage,
    errorHours,
    slotsModified
  } = useBusinessHoursForm5Slots(initialData)

  return (
    <div className={Styles.content}>
      <Separator variant="solid" />
      <Text variant="nextSubtitle">
        {t('account.business-hours.description')}
      </Text>
      {errorMessage && errorHours && (
        <Banner
          className={Styles.errorBanner}
          state="negative"
          type="boxed"
          description={t('account.business-hours.invalid.title')}
          showList
          errorList={Array.isArray(errorHours) ? errorHours : []}
          icon={<InfoCircle />}
        />
      )}
      <Form {...form}>
        <form
          className={Styles.form}
          onSubmit={handleSubmit}
          style={!canEdit ? { pointerEvents: 'none' } : undefined}>
          {form.watch('businessHours').map((weekdayValues, index) => (
            <div className={Styles.itemWrapper} key={weekdayValues.day_name}>
              <Collapsible className={Styles.collapsible}>
                <div className={Styles.collapsibleHeader}>
                  <div className={Styles.collapsibleControl}>
                    <div>
                      <FormField
                        name={`businessHours.${index}.isWorking`}
                        control={form.control}
                        render={({ field }) => {
                          return (
                            <Label
                              layout="row"
                              className={Styles.checkboxLabel}>
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  disabled={!canEdit}
                                  onCheckedChange={(checked: boolean) =>
                                    field.onChange(checked)
                                  }
                                />
                              </FormControl>
                              <Text
                                variant="nextSubtitle"
                                className={Styles.checkboxText}>
                                {t(`common.weekdays.${weekdayValues.day_name}`)}
                                <Text
                                  variant="nextBody"
                                  asChild
                                  className={Styles.checkboxValue}>
                                  <span>
                                    {getFormattedValue(weekdayValues)}
                                  </span>
                                </Text>
                              </Text>
                            </Label>
                          )
                        }}
                      />
                    </div>
                  </div>
                  <CollapsibleTrigger asChild>
                    <button className={Styles.trigger}>
                      <ArrowDown className={Styles.triggerIcon} />
                    </button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  <div className={Styles.itemContent}>
                    {/* Main time slot */}
                    {weekdayValues.slots.length > 0 ? (
                      <div className={Styles.timeItemWithDelete}>
                        <div className={Styles.timeItem}>
                          <FormField
                            name={`businessHours.${index}.start_time`}
                            control={form.control}
                            render={({ field }) => {
                              return (
                                <Label
                                  layout="column"
                                  className={Styles.timeLabel}>
                                  <Text variant="nextBody" weight="semiBold">
                                    {t(startTimeLabelKey)}
                                  </Text>
                                  <TimePicker
                                    {...field}
                                    {...getMixMaxRules(index, null, 'start')}
                                  />
                                </Label>
                              )
                            }}
                          />

                          <FormField
                            name={`businessHours.${index}.end_time`}
                            control={form.control}
                            render={({ field }) => {
                              return (
                                <Label
                                  layout="column"
                                  className={Styles.timeLabel}>
                                  <Text variant="nextBody" weight="semiBold">
                                    {t(endTimeLabelKey)}
                                  </Text>
                                  <TimePicker
                                    {...field}
                                    {...getMixMaxRules(index, null, 'end')}
                                  />
                                </Label>
                              )
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          className={Styles.deleteButton}
                          onClick={() => handleDeleteSlot(index, -1)}
                          aria-label="Delete slot"
                          disabled={!canEdit}>
                          <Close />
                        </button>
                      </div>
                    ) : (
                      <div className={Styles.timeItem}>
                        <FormField
                          name={`businessHours.${index}.start_time`}
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <Label
                                layout="column"
                                className={Styles.timeLabel}>
                                <Text variant="nextBody" weight="semiBold">
                                  {t(startTimeLabelKey)}
                                </Text>
                                <TimePicker
                                  {...field}
                                  {...getMixMaxRules(index, null, 'start')}
                                />
                              </Label>
                            )
                          }}
                        />

                        <FormField
                          name={`businessHours.${index}.end_time`}
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <Label
                                layout="column"
                                className={Styles.timeLabel}>
                                <Text variant="nextBody" weight="semiBold">
                                  {t(endTimeLabelKey)}
                                </Text>
                                <TimePicker
                                  {...field}
                                  {...getMixMaxRules(index, null, 'end')}
                                />
                              </Label>
                            )
                          }}
                        />
                      </div>
                    )}

                    {/* Additional slots (4) */}
                    {weekdayValues.slots.length > 0 &&
                      weekdayValues.slots.map((slot, slotIndex) => (
                        <div
                          className={Styles.timeItemWithDelete}
                          key={slotIndex}>
                          <div className={Styles.timeItem}>
                            <FormField
                              name={`businessHours.${index}.slots.${slotIndex}.start_time`}
                              control={form.control}
                              render={({ field }) => {
                                return (
                                  <Label
                                    layout="column"
                                    className={Styles.timeLabel}>
                                    <Text variant="nextBody" weight="semiBold">
                                      {t(startTimeLabelKey)}
                                    </Text>
                                    <TimePicker
                                      {...field}
                                      {...getMixMaxRules(
                                        index,
                                        slotIndex,
                                        'start'
                                      )}
                                    />
                                  </Label>
                                )
                              }}
                            />

                            <FormField
                              name={`businessHours.${index}.slots.${slotIndex}.end_time`}
                              control={form.control}
                              render={({ field }) => {
                                return (
                                  <Label
                                    layout="column"
                                    className={Styles.timeLabel}>
                                    <Text variant="nextBody" weight="semiBold">
                                      {t(endTimeLabelKey)}
                                    </Text>
                                    <TimePicker
                                      {...field}
                                      {...getMixMaxRules(
                                        index,
                                        slotIndex,
                                        'end'
                                      )}
                                    />
                                  </Label>
                                )
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className={Styles.deleteButton}
                            onClick={() => handleDeleteSlot(index, slotIndex)}
                            aria-label="Delete slot"
                            disabled={!canEdit}>
                            <Close />
                          </button>
                        </div>
                      ))}

                    {weekdayValues.slots.length < 4 && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="medium"
                        icon={<Plus />}
                        onClick={
                          canEdit && !shouldDisableAdd(index)
                            ? () => handleAddSlot(index)
                            : () => {}
                        }
                        disabled={!canEdit || shouldDisableAdd(index)}>
                        {t('account.business-hours.add-slot')}
                      </Button>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          ))}
          <Button
            variant="primary"
            size="medium"
            className={Styles.submit}
            disabled={!canEdit || (!form.formState.isDirty && !slotsModified)}>
            {t('account.business-hours.submit')}
          </Button>
        </form>
      </Form>
    </div>
  )
}

export default BusinessHoursForm5Slots
